// stores/driverAssignStore.js
'use client';

import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { normalizeApiResponse } from '../utils/response';
import { apiOslogCrud } from 'helpers/oslogFormatEndpoint';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const revalidateDriverAssignList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/driver-assign-vehicle/search');
};

const driverAssignApi = apiOslogCrud('driver-assign-vehicle');

const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const response = await driverAssignApi.search(payload);
  return response;
};

export const useDriverAssignList = (page = 0, rowsPerPage = 25, searchKey = 'driver_name', searchValue = '', companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('driver', ['name', 'driver_code'])
      .addJoin('vehicle', ['license_plate', 'name'])
      .setOrder(['id'], false);

    if (searchValue && searchValue.trim() !== '') {
      fb.like(searchKey, searchValue, 'AND', null);
    }

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }
    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/driver-assign-vehicle/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getDriverAssignById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();

    const response = await fleetApi.post(`/driver-assign-vehicle/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;

    return result;
  } catch (error) {
    console.error('Failed to fetch driver assign by ID:', error);
    throw error;
  }
};

export const createDriverAssign = async (formData = {}) => {
  try {
    const response = await fleetApi.post(`/driver-assign-vehicle/new${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverAssignList();
    return response.data;
  } catch (error) {
    console.error('Error creating driver assign:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateDriverAssign = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/driver-assign-vehicle/edit/${id}${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverAssignList();
    return response.data;
  } catch (error) {
    console.error('Error updating driver assign:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteDriverAssign = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/driver-assign-vehicle/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverAssignList();
    return response.data;
  } catch (error) {
    console.error('Error deleting driver assign:', error?.response?.data ?? error);
    throw error;
  }
};
